import {useCallback, useEffect, useRef, useState} from 'react'
import axios from 'axios'
import {usersOnlineUrl} from '../redux/api'

/** Refresh once per minute — auto-refresh for status only, avoid spamming the API */
const REFRESH_INTERVAL_MS = 60_000

/** Response from GET /api/users/online (admin only) */
interface UsersOnlineResponse {
  online: string[]
  count: number
}

/**
 * Fetches connected users (count + IDs) for admin (role 2) only.
 * Per docs/USER-SOCKET-IO.md: GET /api/users/online returns { online, count }.
 * Only call this when me?.role === '2'.
 */
export function useConnectedUsersCount(isAdmin: boolean) {
  const [count, setCount] = useState<number | null>(null)
  const [onlineIds, setOnlineIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchCount = useCallback(async () => {
    if (!isAdmin) return
    
    // Check network status before making request
    if (!navigator.onLine) {
      // Network is offline - silently skip this fetch, will retry when online
      if (process.env.NODE_ENV === 'development') {
        console.debug('Skipping fetch: network offline')
      }
      return
    }
    
    setLoading(true)
    setError(null)
    try {
      const res = await axios.get<UsersOnlineResponse>(usersOnlineUrl)
      setCount(res.data?.count ?? 0)
      setOnlineIds(Array.isArray(res.data?.online) ? res.data.online : [])
      setError(null) // Clear any previous errors on success
    } catch (e: any) {
      // Handle 403 (permission denied) - stop polling
      if (e?.response?.status === 403) {
        setCount(null)
        setOnlineIds([])
        setError(null)
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        return
      }
      
      // Handle network errors (ERR_INTERNET_DISCONNECTED, ERR_NETWORK_CHANGED, etc.)
      const isNetworkError =
        e?.code === 'ERR_NETWORK' ||
        e?.code === 'ERR_INTERNET_DISCONNECTED' ||
        e?.message?.includes('ERR_INTERNET_DISCONNECTED') ||
        e?.message?.includes('ERR_NETWORK_CHANGED') ||
        e?.message?.includes('Network Error') ||
        (!e?.response && e?.request) // Request made but no response (network issue)
      
      if (isNetworkError) {
        // Network error - don't show error to user, keep previous data, will retry
        if (process.env.NODE_ENV === 'development') {
          console.debug('Network error fetching online users, will retry:', e?.message)
        }
        // Don't clear count/onlineIds - keep showing last known state
        // Don't set error - network issues are temporary
        setLoading(false)
        return
      }
      
      // Other errors - show error but keep polling
      setCount(null)
      setOnlineIds([])
      setError(e?.response?.data?.message || e?.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [isAdmin])

  useEffect(() => {
    if (!isAdmin) {
      setCount(null)
      setOnlineIds([])
      setError(null)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }
    
    // Initial fetch
    fetchCount()
    
    // Set up polling interval
    intervalRef.current = setInterval(fetchCount, REFRESH_INTERVAL_MS)
    
    // Retry immediately when connection is restored
    const handleOnline = () => {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Network restored, fetching online users')
      }
      fetchCount()
    }
    
    window.addEventListener('online', handleOnline)
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      window.removeEventListener('online', handleOnline)
    }
  }, [isAdmin, fetchCount])

  return {count, onlineIds, loading, error}
}
